import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../../components/ui/Header";
import TrekHero from "./components/TrekHero";
import TrekOverview from "./components/TrekOverview";
import DetailedItinerary from "./components/DetailedItinerary";
import ElevationProfile from "./components/ElevationProfile";
import CulturalEncounters from "./components/CulturalEncounters";
import InclusionsExclusions from "./components/InclusionsExclusions";
import PreparationGuide from "./components/PreparationGuide";
import PhotoGallery from "./components/PhotoGallery";
import TrekTestimonials from "./components/TrekTestimonials";
import BookingSection from "./components/BookingSection";
import SimilarTreks from "./components/SimilarTreks";
import Icon from "../../components/AppIcon";
import { trekApi } from "../../services/api";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import ErrorMessage from "../../components/ui/ErrorMessage";
import { getTrekConfig } from "../../../trekConfig";

const TrekPackageDetails = () => {
  const { trekId } = useParams(); // Get trekId from URL params
  const navigate = useNavigate();
  const [currentYear] = useState(new Date().getFullYear());

  const [trek, setTrek] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch trek data when component mounts or trekId changes
  useEffect(() => {
    const fetchTrekData = async () => {
      try {
        setLoading(true);
        const response = await trekApi.getTrekById(trekId);

        // Fix image URLs once (same pattern as listing page)
        const backendUrl =
          import.meta.env.VITE_BACKEND_URL || "http://localhost:5001";

        const fixImagePath = (path) =>
          path?.startsWith("/") ? `${backendUrl}${path}` : path;

        const fixedTrek = {
          ...response,
          trekData: {
            ...response.trekData,
            heroImage: fixImagePath(response.trekData?.heroImage),
          },
          culturalHighlights: response.culturalHighlights?.map((h) => ({
            ...h,
            image: fixImagePath(h.image),
          })),
          photos: response.photos?.map((p) => ({
            ...p,
            src: fixImagePath(p.src),
          })),
        };

        setTrek(fixedTrek);
      } catch (err) {
        setError(err.message || "Failed to load trek details");
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    if (trekId) fetchTrekData();
  }, [trekId]);

  const handleBookNow = () => {
    navigate("/booking-consultation-center", {
      state: {
        trekId,
        trekName: trek?.trekData?.name,
        trekPrice: trek?.trekData?.price,
      },
    });
  };

  const handleConsultation = () => {
    navigate("/booking-consultation-center", {
      state: {
        trekId,
        trekName: trek?.trekData?.name,
      },
    });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-24 flex justify-center">
          <LoadingSpinner size="lg" text={`Loading ${trekId} details...`} />
        </div>
      </div>
    );
  }

  if (error || !trek) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-24 px-4 text-center">
          <ErrorMessage
            message={error || "Trek not found"}
            onRetry={() => window.location.reload()}
          />
          <button
            onClick={() => navigate("/trek-discovery-hub")}
            className="mt-6 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark"
          >
            Back to All Treks
          </button>
        </div>
      </div>
    );
  }
  // No trek found
  if (!trek) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-24 text-center px-4">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">
            Trek Not Found
          </h1>
          <p className="text-gray-600 mb-6">
            The trek you're looking for doesn't exist.
          </p>
          <button
            onClick={() => navigate("/")}
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition"
          >
            Browse All Treks
          </button>
        </div>
      </div>
    );
  }

  // Destructure the full data structure you're using
  const {
    trekData: mainInfo,
    itineraryData,
    elevationData,
    culturalHighlights,
    inclusions,
    exclusions,
    preparationData,
    photos,
    testimonials,
    similarTreks,
    trekStatistics,
    quickFacts,
  } = trek;

  // console.log(trek);

  return (
    <div className="min-h-screen bg-[#fafaf7]">
      <Header />

      <main className="pt-24 lg:pt-28">
        <TrekHero
          trek={mainInfo}
          onBookNow={handleBookNow}
          onConsultation={handleConsultation}
        />

        <TrekOverview
          trek={mainInfo}
          quickFacts={quickFacts}
          statistics={trekStatistics}
        />

        <DetailedItinerary itinerary={itineraryData} />

        {elevationData && elevationData.length > 0 && (
          <ElevationProfile elevationData={elevationData} trek={mainInfo} />
        )}

        {culturalHighlights && culturalHighlights.length > 0 && (
          <CulturalEncounters culturalHighlights={culturalHighlights} />
        )}

        {testimonials && testimonials.length > 0 && (
          <TrekTestimonials testimonials={testimonials} />
        )}

        {(inclusions || exclusions) && (
          <InclusionsExclusions
            inclusions={inclusions}
            exclusions={exclusions}
          />
        )}

        {preparationData && (
          <PreparationGuide preparationData={preparationData} />
        )}

        {photos && photos.length > 0 && <PhotoGallery photos={photos} />}

        {similarTreks && similarTreks.length > 0 && (
          <SimilarTreks similarTreks={similarTreks} />
        )}

        <BookingSection trek={mainInfo} />

      </main>

      <footer className="bg-foreground text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 mountain-gradient rounded-lg flex items-center justify-center">
                  <img src="/assets/images/logo.jpeg" alt="Sherpa Trekker Logo" />
                </div>
                <div>
                  <span className="font-headline font-bold text-xl text-white">
                    Sherpa Trekker
                  </span>
                  <p className="text-sm text-white/60">
                    Where Adventure Meets Authenticity
                  </p>
                </div>
              </div>
              <p className="text-white/60 mb-4 leading-relaxed text-sm">
                Experience the Himalayas through the eyes of those who call it
                home. Our expert Sherpa guides bring generations of mountain
                wisdom to every adventure.
              </p>
              <div className="flex gap-4">
                <Icon name="Facebook" size={20} className="text-white/40 hover:text-white cursor-pointer transition-colors" />
                <Icon name="Instagram" size={20} className="text-white/40 hover:text-white cursor-pointer transition-colors" />
                <Icon name="Twitter" size={20} className="text-white/40 hover:text-white cursor-pointer transition-colors" />
                <Icon name="Youtube" size={20} className="text-white/40 hover:text-white cursor-pointer transition-colors" />
              </div>
            </div>

            <div>
              <h3 className="font-headline font-semibold text-white mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li><a href="/trek-discovery-hub" className="text-white/60 hover:text-white transition-colors text-sm">Discover Treks</a></li>
                <li><a href="/activities" className="text-white/60 hover:text-white transition-colors text-sm">Activities & Tours</a></li>
                <li><a href="/nepal-visa-information" className="text-white/60 hover:text-white transition-colors text-sm">Nepal Travel Info</a></li>
                <li><a href="/sherpa-guide-profiles" className="text-white/60 hover:text-white transition-colors text-sm">Our Guides</a></li>
                <li><a href="/traveler-stories-hub" className="text-white/60 hover:text-white transition-colors text-sm">Traveler Stories</a></li>
                <li><a href="/booking-consultation-center" className="text-white/60 hover:text-white transition-colors text-sm">Book Consultation</a></li>
              </ul>
            </div>

            <div>
              <h3 className="font-headline font-semibold text-white mb-4">Contact Us</h3>
              <div className="space-y-2 text-white/60 text-sm">
                <div className="flex items-center gap-2">
                  <Icon name="Phone" size={14} />
                  <span>+977-9851157213</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="Mail" size={14} />
                  <span>imsherpatrekker@gmail.com</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="MapPin" size={14} />
                  <span>Thamel, Kathmandu, Nepal</span>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 mt-8 pt-8 text-center text-white/40 text-sm">
            <p>&copy; {currentYear} Sherpa Trekker. All rights reserved. | Licensed Trek Operator in Nepal</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TrekPackageDetails;
